<template>
  <v-dialog overlay-opacity="0.6" persistent max-width="900" :value="value">
    <v-container fill-height fluid grid-list-xl>
      <v-layout justify-center wrap>
        <v-flex md12>
          <base-material-card class="pa-0" color="blue">
            <template v-slot:heading>
              <v-row class="pa-0">
                <v-col cols="1" class="pa-0">
                  <v-icon large>mdi-account-multiple-plus-outline</v-icon>
                </v-col>
                <v-col
                  align="center"
                  cols="10"
                  class="pa-0 card-title-text"
                  style="font-size: 25px"
                >
                  {{ localAssistant.id ? $t("automazione-dialog-edit") : $t("automazione-dialog-new") }}
                </v-col>
                <v-col cols="1" class="pa-0">
                  <v-icon @click="closeDialog" large>mdi-close</v-icon>
                </v-col>
              </v-row>
            </template>

            <v-card-text>
              <v-row dense>
                <v-col cols="6">
                  <v-text-field
                    outlined
                    dense
                    :label="$t('automazione-operator-name')"
                    v-model="localAssistant.name"
                  />
                </v-col>
                <v-col cols="6" />
              </v-row>

              <v-row dense>
                <v-col cols="4">
                  <v-autocomplete
                    outlined
                    dense
                    clearable
                    hide-selected
                    :loading="openRouterLoading"
                    :items="providers"
                    item-text="name"
                    item-value="id"
                    :label="$t('automazione-operator-provider')"
                    :hint="$t('automazione-operator-provider-hint')"
                    class="param-field param-field-select"
                    persistent-hint
                    v-model="localAssistant.provider"
                  />
                </v-col>
                <v-col cols="4">
                  <v-autocomplete
                    outlined
                    dense
                    clearable
                    hide-selected
                    :loading="openRouterLoading"
                    :items="filteredModels"
                    item-text="label"
                    item-value="id"
                    :label="$t('automazione-operator-model')"
                    :hint="$t('automazione-operator-model-hint')"
                    class="param-field param-field-select"
                    persistent-hint
                    v-model="localAssistant.model"
                  />
                </v-col>
                <v-col cols="4">
                  <v-text-field
                    outlined
                    dense
                    type="number"
                    min="0"
                    max="2"
                    step="0.1"
                    :label="$t('automazione-operator-temperature')"
                    :hint="$t('automazione-operator-temperature-hint')"
                    class="param-field param-field-select"
                    persistent-hint
                    v-model.number="localAssistant.temperature"
                  />
                </v-col>
              </v-row>

              <v-row dense>
                <v-col>
                  <v-textarea
                    outlined
                    dense
                    rows="6"
                    :label="$t('automazione-operator-instructions')"
                    v-model="localAssistant.instructions"
                  />
                </v-col>
              </v-row>

              <v-row dense>
                <v-col class="text-right">
                  <v-btn text @click="closeDialog">{{ $t("gbl-cancel") }}</v-btn>
                  <v-btn color="primary" @click="saveAssistant">{{ $t("gbl-ok") }}</v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </base-material-card>
        </v-flex>
      </v-layout>
    </v-container>
  </v-dialog>
</template>

<script>
import automazione from "../../apis/automazione";

export default {
  props: {
    value: {
      type: Boolean,
      default: false
    },
    assistant: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      localAssistant: this.defaultAssistant(),
      syncingEditorState: false,
      openRouterLoading: false,
      providers: [],
      models: []
    };
  },

  computed: {
    filteredModels() {
      const provider = this.normalizeProvider(this.localAssistant.provider);
      const scopedModels = !provider
        ? this.models.slice()
        : this.models.filter(i => i.provider === provider);

      const selectedModel = (this.localAssistant.model || "").trim();
      if (!selectedModel.length) return scopedModels;

      const alreadyPresent = scopedModels.some(i => i.id === selectedModel);
      if (alreadyPresent) return scopedModels;

      // Keep current saved/manual model visible even if catalog is delayed or missing it.
      return [
        {
          id: selectedModel,
          name: selectedModel,
          label: selectedModel + " [saved]",
          provider: provider || ""
        },
        ...scopedModels
      ];
    }
  },

  watch: {
    value(val) {
      if (val) {
        this.syncingEditorState = true;
        this.syncLocalAssistant();
        this.fetchOpenRouterCatalog().finally(() => {
          this.syncingEditorState = false;
          this.syncProviderForModel();
          this.syncModelForProvider();
        });
      }
    },

    assistant: {
      handler() {
        if (!this.value) return;
        this.syncingEditorState = true;
        this.syncLocalAssistant();
        this.$nextTick(() => {
          this.syncingEditorState = false;
          this.syncProviderForModel();
          this.syncModelForProvider();
        });
      },
      deep: true
    },

    "localAssistant.model"() {
      if (!this.value || this.syncingEditorState) return;
      this.syncProviderForModel();
    },

    "localAssistant.provider"() {
      if (!this.value || this.syncingEditorState) return;
      this.syncModelForProvider();
    }
  },

  methods: {
    defaultAssistant() {
      return {
        id: null,
        name: "",
        instructions: "",
        provider: "",
        model: "",
        temperature: 0.2
      };
    },

    syncLocalAssistant() {
      this.localAssistant = Object.assign(
        this.defaultAssistant(),
        this.assistant ? this.assistant : {}
      );
      this.localAssistant.provider = this.normalizeProvider(
        this.localAssistant.provider
      );
    },

    fetchOpenRouterCatalog() {
      this.openRouterLoading = true;
      return automazione
        .fetchOpenRouterCatalog()
        .then(result => {
          this.providers = (result.providers || []).map(i => ({
            id: i.id || i.name,
            name: i.name || i.id
          }));
          this.models = (result.models || []).map(i => ({
            id: i.id,
            name: i.name || i.id,
            label:
              i.id +
              (i.name && i.name !== i.id ? " (" + i.name + ")" : "") +
              (i.is_canonical_version ? " [version]" : "") +
              (i.is_latest_alias ? " [latest alias]" : ""),
            provider: i.provider || "",
            isCanonicalVersion: !!i.is_canonical_version,
            isLatestAlias: !!i.is_latest_alias
          }));

          this.syncProviderForModel();
          this.syncModelForProvider();
        })
        .catch(err => {
          // Keep previous catalog cache if request fails.
          console.log("[automazione] OpenRouter catalog error", err);
        })
        .finally(() => {
          this.openRouterLoading = false;
        });
    },

    syncProviderForModel() {
      if (!this.localAssistant.model || this.localAssistant.provider) return;
      const model = this.models.find(i => i.id === this.localAssistant.model);
      if (model && model.provider) {
        this.localAssistant.provider = model.provider;
      }
    },

    syncModelForProvider() {
      const provider = this.normalizeProvider(this.localAssistant.provider);
      if (!this.localAssistant.model || !provider) return;
      // Do not clear model while catalog is still loading or unavailable.
      if (this.openRouterLoading || !this.models.length) return;

      const providerModels = this.models.filter(i => i.provider === provider);
      if (!providerModels.length) {
        // Keep current value if provider has no catalog entries (manual/legacy model).
        return;
      }

      const valid = providerModels.find(i => i.id === this.localAssistant.model);
      if (!valid) {
        const modelInCatalog = this.models.find(i => i.id === this.localAssistant.model);
        if (modelInCatalog && modelInCatalog.provider) {
          this.localAssistant.provider = modelInCatalog.provider;
        }
      }
    },

    normalizeProvider(provider) {
      if (!provider) return "";
      if (typeof provider === "string") return provider;
      if (typeof provider === "object") return provider.id || provider.name || "";
      return "";
    },

    closeDialog() {
      this.$emit("input", false);
    },

    saveAssistant() {
      if (!this.localAssistant.name || !this.localAssistant.name.trim().length) {
        return;
      }

      // Preserve assignment/behavior fields from existing record.
      const base = this.assistant ? Object.assign({}, this.assistant) : {};
      this.$emit("onSave", Object.assign(base, this.localAssistant));
    }
  }
};
</script>

<style scoped>
.param-field {
  margin-top: 6px;
  margin-bottom: 10px;
}

.param-field-select {
  padding-bottom: 10px;
}
</style>
